import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import {
  countQuery,
  countSubrequest,
  ITEMS_PER_PERIOD,
  itemsSubrequest,
  normalizePath,
  periodOfPage,
  queryAsIs,
  querystringKey,
  selectedPeriods,
  vocabularyValues,
} from './periodTabs';

/**
 * Server-side loading for a period-tabs listing.
 *
 * Without this the server renders the block's plain first batch and the
 * browser then replaces it - a visible jump, and a crawler following a tab's
 * URL would be served the wrong period. It runs as one sequence rather than
 * a handful of parallel requests, because each step decides the next: which
 * periods are worth offering is only known once the counts are in, and what
 * to list is only known from those.
 */
export const periodTabsAsyncData = (spec) =>
  function getAsyncData({ data, path, location, dispatch, store }) {
    const querystring = data?.querystring;
    if (!querystring) {
      return [async () => null];
    }
    const requestPath = getBaseUrl(path);
    const base = normalizePath(data.tab_pages_base);
    const linkTabs = !!(base && spec.segment);
    const onPeriodPage = !!periodOfPage(spec, base, requestPath);
    const key = querystringKey(querystring, base, spec.name);

    const load = async () => {
      let periods = spec.periods(querystring);
      if (!periods.length && spec.vocabulary) {
        // The candidates come from a vocabulary, and it has to be here
        // before there is anything to count.
        await dispatch(
          getVocabulary({ vocabNameOrURL: spec.vocabulary, size: -1 }),
        );
        periods = spec.periods(
          querystring,
          vocabularyValues(store?.getState(), spec.vocabulary),
        );
      }
      const scope = spec.scope
        ? spec.scope(querystring, base)
        : queryAsIs(querystring);

      await Promise.allSettled(
        periods.map((period) =>
          dispatch(
            getQueryStringResults(
              requestPath,
              countQuery(spec, scope, period),
              countSubrequest(key, period),
            ),
          ),
        ),
      );

      let shown = null;
      if (!onPeriodPage) {
        // Select from the periods that actually have something, exactly as
        // the component does once its counts are in, so the two agree and
        // the browser has nothing to correct.
        const subrequests =
          store?.getState()?.querystringsearch?.subrequests || {};
        const withResults = periods.filter(
          (p) => (subrequests[countSubrequest(key, p)]?.total ?? 0) > 0,
        );
        shown = selectedPeriods(
          spec,
          linkTabs ? '' : location?.search,
          withResults,
          data.tab_count,
        );
        if (!shown.length) return null;
      }

      const q = queryAsIs(querystring);
      return dispatch(
        getQueryStringResults(
          requestPath,
          {
            ...(onPeriodPage
              ? spec.withoutPeriod
                ? spec.withoutPeriod(q)
                : q
              : spec.narrow(q, shown)),
            b_size: ITEMS_PER_PERIOD,
            metadata_fields: '_all',
          },
          itemsSubrequest(key, onPeriodPage ? 'page' : shown.join('|')),
        ),
      );
    };

    return [load()];
  };

export default periodTabsAsyncData;
